import { useCallback, useEffect, useRef, useState } from "react";
import type { LiveSession, MediaAsset, ScheduleSlot, Settings, StationUser, UserRole } from "@ees/shared";
import { Room, RoomEvent } from "livekit-client";
import { api, describeLive, type RemoteSyncStatus } from "../api.js";
import { authorizationValue, clearCredentials, setCredentials } from "../auth.js";

type Tab = "library" | "schedule" | "go live" | "settings";

interface ScheduleResponse {
  slots: ScheduleSlot[];
  timezone: string;
  upcoming: { slot: ScheduleSlot; startsAt: string; endsAt: string }[];
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function minutesToTime(minute: number): string {
  const normalised = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalised / 60)).padStart(2, "0")}:${String(normalised % 60).padStart(2, "0")}`;
}

function describeSlot(slot: ScheduleSlot): string {
  const days =
    slot.dayOfWeek.length === 0
      ? "Daily"
      : slot.dayOfWeek.length === 7
        ? "Daily"
        : slot.dayOfWeek
            .slice()
            .sort()
            .map((day) => DAY_NAMES[day])
            .join(", ");
  return `${days} · ${minutesToTime(slot.startMinute)}–${minutesToTime(slot.startMinute + slot.durationMinutes)}`;
}

function LibraryTab(): JSX.Element {
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /** iDrive sync state; only shown when a remote is configured. */
  const [remote, setRemote] = useState<RemoteSyncStatus | null>(null);

  const refreshRemote = useCallback(async () => {
    try {
      setRemote(await api.remoteStatus());
    } catch {
      // Transient poll failure; the next tick retries.
    }
  }, []);

  useEffect(() => {
    void refreshRemote();
    const timer = setInterval(() => void refreshRemote(), 5_000);
    return () => clearInterval(timer);
  }, [refreshRemote]);

  const refresh = useCallback(async () => {
    const { items: loaded } = await api.media({ limit: 200 });
    setItems(loaded);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function analyse() {
    setBusy(true);
    setMessage(null);
    try {
      const { analysed } = await api.analyse("all");
      setMessage(analysed === 0 ? "Everything is already analysed." : `Analysed ${analysed} file(s).`);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Analysis failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card__body stack">
      <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
        <button className="button button--primary" onClick={() => void analyse()} disabled={busy}>
          {busy ? "Analysing…" : "Analyse tempo & loudness"}
        </button>
        <button className="button" onClick={() => void refresh()}>
          Refresh
        </button>
        {remote?.enabled ? (
          <button
            className="button"
            disabled={remote.running}
            onClick={() => {
              void api
                .syncRemote()
                .then(() => refreshRemote())
                .catch(() => undefined);
            }}
          >
            {remote.running ? "Syncing iDrive…" : "Sync with iDrive"}
          </button>
        ) : null}
        {message ? <span className="field__hint">{message}</span> : null}
      </div>

      {remote?.enabled && remote.last ? (
        <span className="field__hint">
          {remote.last.ok ? "iDrive sync OK" : "iDrive sync failed"} · {remote.last.mode} ·{" "}
          {Math.round(remote.last.durationMs / 1000)}s
          {remote.last.ok ? "" : ` · ${remote.last.error ?? remote.last.reason}`}
        </span>
      ) : null}

      {items.length === 0 ? (
        <p className="empty">
          No media found. Drop audio or video into the station media folder, then refresh.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Artist</th>
              <th>Kind</th>
              <th>BPM</th>
              <th>Loudness</th>
              <th>Length</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.title}</td>
                <td>{item.artist ?? "—"}</td>
                <td>{item.kind}</td>
                <td className="num">{item.bpm ? item.bpm.toFixed(1) : "—"}</td>
                <td className="num">
                  {item.loudnessLufs ? `${item.loudnessLufs.toFixed(1)} LUFS` : "—"}
                </td>
                <td className="num">{Math.round(item.durationMs / 1000)}s</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ScheduleTab(): JSX.Element {
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setData(await api.schedule());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load the schedule.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) return <div className="card__body banner banner--error">{error}</div>;
  if (!data) return <div className="card__body empty">Loading schedule…</div>;

  return (
    <div className="card__body stack">
      <span className="field__hint">Times are station-local ({data.timezone}).</span>
      {data.slots.length === 0 ? (
        <p className="empty">
          No slots yet. With no schedule the station plays its whole library as an always-on
          rotation.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>When</th>
              <th>Priority</th>
              <th>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {data.slots.map((slot) => (
              <tr key={slot.id}>
                <td>{slot.name}</td>
                <td>{slot.kind}</td>
                <td>{describeSlot(slot)}</td>
                <td className="num">{slot.priority}</td>
                <td>{slot.enabled ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SettingsTab(): JSX.Element {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.settings().then(setSettings).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : "Could not load settings.");
    });
  }, []);

  async function save() {
    if (!settings) return;
    try {
      setSettings(await api.saveSettings(settings));
      setSaved("Settings applied.");
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save settings.");
    }
  }

  if (error) return <div className="card__body banner banner--error">{error}</div>;
  if (!settings) return <div className="card__body empty">Loading settings…</div>;

  return (
    <div className="card__body form">
      <div className="field">
        <label className="field__label" htmlFor="stationName">
          Station name
        </label>
        <input
          id="stationName"
          type="text"
          value={settings.stationName}
          onChange={(event) => setSettings({ ...settings, stationName: event.target.value })}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="crossfade">
          Crossfade length (seconds)
        </label>
        <input
          id="crossfade"
          type="number"
          min={0}
          max={20}
          step={0.5}
          value={settings.crossfadeSeconds}
          onChange={(event) =>
            setSettings({ ...settings, crossfadeSeconds: Number(event.target.value) })
          }
        />
        <span className="field__hint">
          {settings.beatAligned
            ? "Crossfades snap to bar boundaries when the tracks share a tempo."
            : "Crossfades use a fixed length."}
        </span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="fallbackBpm">
          Fallback BPM
        </label>
        <input
          id="fallbackBpm"
          type="number"
          min={60}
          max={220}
          value={settings.fallbackBpm}
          onChange={(event) => setSettings({ ...settings, fallbackBpm: Number(event.target.value) })}
        />
        <span className="field__hint">Used for tracks with no detected tempo. 128 suits most EDM.</span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="stationId">
          Station ident cadence
        </label>
        <input
          id="stationId"
          type="number"
          min={0}
          max={50}
          value={settings.stationIdEveryTracks}
          onChange={(event) =>
            setSettings({ ...settings, stationIdEveryTracks: Number(event.target.value) })
          }
        />
        <span className="field__hint">
          Play an ident after this many tracks. 0 turns idents off.
        </span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="enhancerEnabled">
          EDM enhancer
        </label>
        <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", cursor: "pointer" }}>
          <input
            id="enhancerEnabled"
            type="checkbox"
            checked={settings.enhancerEnabled}
            onChange={(event) =>
              setSettings({ ...settings, enhancerEnabled: event.target.checked })
            }
          />
          <span>Enabled</span>
        </label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={settings.enhancerAmount}
          disabled={!settings.enhancerEnabled}
          onChange={(event) =>
            setSettings({ ...settings, enhancerAmount: Number(event.target.value) })
          }
          aria-label="Enhancer amount"
        />
        <span className="field__hint">
          Bass weight, glue compression, stereo width and air. Amount{" "}
          {Math.round(settings.enhancerAmount * 100)}% — 0 is a bit-exact bypass.
        </span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="loudnessNormEnabled">
          Output loudness
        </label>
        <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", cursor: "pointer" }}>
          <input
            id="loudnessNormEnabled"
            type="checkbox"
            checked={settings.loudnessNormEnabled}
            onChange={(event) =>
              setSettings({ ...settings, loudnessNormEnabled: event.target.checked })
            }
          />
          <span>Enabled</span>
        </label>
        <input
          id="targetLufs"
          type="number"
          min={-30}
          max={-5}
          step={0.5}
          value={settings.targetLufs}
          disabled={!settings.loudnessNormEnabled}
          onChange={(event) =>
            setSettings({ ...settings, targetLufs: Number(event.target.value) })
          }
          aria-label="Target loudness in LUFS"
        />
        <span className="field__hint">
          Holds the stream at {settings.targetLufs} LUFS with slow, bounded gain: quiet rips and
          live feeds are levelled, drops and intros are left alone. −14 matches streaming services.
        </span>
      </div>

      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
        <button className="button button--primary" onClick={() => void save()}>
          Save settings
        </button>
        {saved ? <span className="field__hint">{saved}</span> : null}
      </div>

      <UsersPanel />
    </div>
  );
}

/**
 * Console accounts: list, add and remove — admin only.
 *
 * Visibility is decided by the server (`/api/console/config` returns the
 * caller's role), so a DJ simply never sees the panel; the endpoints check
 * again anyway, so hiding is a courtesy rather than the security boundary.
 */
function UsersPanel(): JSX.Element {
  const [visible, setVisible] = useState(false);
  const [users, setUsers] = useState<StationUser[]>([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("dj");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    void api
      .users()
      .then((result) => setUsers(result.users))
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load users.");
      });
  }, []);

  useEffect(() => {
    void api
      .consoleConfig()
      .then((config) => {
        if (config.user.role === "admin") {
          setVisible(true);
          refresh();
        }
      })
      .catch(() => undefined);
  }, [refresh]);

  async function add(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      const created = await api.createUser({ username, password, role });
      setNote(`${created.username} can now sign in as ${created.role === "admin" ? "admin" : "DJ"}.`);
      setUsername("");
      setPassword("");
      setRole("dj");
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not add the user.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(user: StationUser): Promise<void> {
    setNote(null);
    setError(null);
    if (!window.confirm(`Remove ${user.username}? Their password stops working immediately.`)) {
      return;
    }
    try {
      await api.deleteUser(user.id);
      setNote(`${user.username} removed.`);
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not remove the user.");
    }
  }

  if (!visible) return <></>;

  const canAdd = username.trim().length >= 3 && password.length >= 8 && !busy;

  return (
    <div className="field">
      <span className="field__label">Users</span>
      <span className="field__hint">
        Accounts that sign in to this console. Admins can add and remove people; DJs get the
        console without user management. The built-in admin from the server config always works.
      </span>
      {users.length > 0 ? (
        <ul style={{ listStyle: "none", display: "grid", gap: "0.4rem", margin: 0, padding: 0 }}>
          {users.map((user) => (
            <li key={user.id} style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: "0.8rem" }}>{user.username}</span>
              <span className="field__hint">{user.role}</span>
              <button className="button" onClick={() => void remove(user)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="field__hint">No extra accounts yet.</span>
      )}
      <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "grid", gap: "0.25rem" }}>
          <span className="field__hint">Username</span>
          <input
            type="text"
            value={username}
            autoComplete="off"
            onChange={(event) => setUsername(event.target.value)}
          />
        </label>
        <label style={{ display: "grid", gap: "0.25rem" }}>
          <span className="field__hint">Password</span>
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <label style={{ display: "grid", gap: "0.25rem" }}>
          <span className="field__hint">Role</span>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value === "admin" ? "admin" : "dj")}
          >
            <option value="dj">DJ</option>
            <option value="admin">Admin</option>
          </select>
        </label>
        <button className="button button--primary" onClick={() => void add()} disabled={!canAdd}>
          Add user
        </button>
      </div>
      {note ? <span className="field__hint">{note}</span> : null}
      {error ? (
        <div className="banner banner--error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Pull JPEG frames off a camera (or screen) track and push them to the station.
 *
 * Only one frame is ever in flight: the canvas is not drawn again until the
 * previous blob has been sent, so frames can never arrive out of order, and a
 * backed-up socket skips frames instead of building latency. Capped at 1280 px
 * wide — plenty for the 720p ladder and kinder to a DJ's upload.
 */
function startFramePump(video: HTMLVideoElement, socket: WebSocket): number {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  let busy = false;
  /** Socket buffer beyond which frames are skipped rather than queued. */
  const MAX_BUFFERED_BYTES = 1_000_000;

  return window.setInterval(() => {
    if (busy || !context || socket.readyState !== WebSocket.OPEN) return;
    if (socket.bufferedAmount > MAX_BUFFERED_BYTES) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height || video.readyState < 2) return;

    const scale = Math.min(1, 1280 / width);
    const targetW = Math.round((width * scale) / 2) * 2;
    const targetH = Math.round((height * scale) / 2) * 2;
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }

    busy = true;
    context.drawImage(video, 0, 0, targetW, targetH);
    canvas.toBlob((blob) => {
      if (!blob) {
        busy = false;
        return;
      }
      void blob
        .arrayBuffer()
        .then((buffer) => {
          if (socket.readyState === WebSocket.OPEN && socket.bufferedAmount <= MAX_BUFFERED_BYTES) {
            socket.send(buffer);
          }
        })
        .catch(() => undefined)
        .finally(() => {
          busy = false;
        });
    }, "image/jpeg", 0.7);
  }, Math.round(1000 / 12));
}

/**
 * Local monitor volume: unity gain — the mic path already runs through the
 * browser's AGC, so what the DJ hears matches what the station receives.
 */
const MONITOR_GAIN = 1;

/**
 * Go-live: broadcast straight from this browser into the master bus.
 *
 * With LiveKit configured (the normal deployment) the console joins the shared
 * studio room over WebRTC and publishes mic/camera tracks — the same room an
 * OBS DJ reaches over WHIP — and the station's egress worker feeds the room
 * back into the RTMP ingest. Where LiveKit is not configured (local dev), the
 * original direct path still works: audio is converted to interleaved PCM16 @
 * 48 kHz by an AudioWorklet and pushed over /ws/live-input, with camera frames
 * JPEG-encoded over /ws/live-video beside it.
 */
function GoLiveTab(): JSX.Element {
  const [live, setLive] = useState<LiveSession | null>(null);
  const [phase, setPhase] = useState<"idle" | "starting" | "onair" | "error">("idle");
  const [level, setLevel] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [source, setSource] = useState<"mic" | "camera" | "tab">("mic");
  /** Hear your own input locally; off by default so speakers cannot howl. */
  const [monitor, setMonitor] = useState(false);
  const sessionRef = useRef<{
    kind: "legacy" | "livekit";
    ws?: WebSocket;
    room?: Room;
    ctx: AudioContext;
    stream: MediaStream;
    videoWs?: WebSocket | null;
    videoEl?: HTMLVideoElement | null;
    frameTimer?: number | null;
    monitorGain: GainNode | null;
    meter?: number | null;
  } | null>(null);
  /** LiveKit availability from /api/live/config; fetched once on mount. */
  const [livekit, setLivekit] = useState<{ enabled: boolean; url: string; room: string } | null>(null);
  /** Cached WHIP endpoint once the OBS card has requested it. */
  const [whip, setWhip] = useState<{ url: string; streamKey: string } | null>(null);
  const [whipBusy, setWhipBusy] = useState(false);
  const [whipError, setWhipError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .liveConfig()
      .then((cfg) => {
        if (!cancelled) setLivekit(cfg.livekit);
      })
      .catch(() => {
        // No config: the tab still works against the legacy input path.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await api.status();
        if (!cancelled) setLive(status.live);
      } catch {
        // Transient poll failure; the next tick retries.
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), 2_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const stop = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      if (session.frameTimer != null) window.clearInterval(session.frameTimer);
      if (session.meter != null) window.clearInterval(session.meter);
      if (session.videoEl) {
        session.videoEl.pause();
        session.videoEl.srcObject = null;
      }
      try {
        session.videoWs?.close();
      } catch {
        // Already closed.
      }
      try {
        session.ws?.close();
      } catch {
        // Already closed.
      }
      // Leaving the room is what lets the station's egress worker see the room
      // empty, stop feeding the ingest and hand the station back to automation.
      try {
        void session.room?.disconnect();
      } catch {
        // Already disconnected.
      }
      for (const track of session.stream.getTracks()) track.stop();
      void session.ctx.close().catch(() => undefined);
    }
    setPhase("idle");
    setLevel(0);
  }, []);

  // Never leave a microphone or tab capture running when the console unmounts.
  useEffect(() => () => stop(), [stop]);

  // Apply the monitor preference to the live session — and re-apply whenever
  // the phase changes, so a toggle flipped while still connecting takes effect
  // the moment the session exists.
  useEffect(() => {
    const gain = sessionRef.current?.monitorGain;
    if (!gain) return;
    // A short ramp instead of a step: switching mid-set must never click.
    gain.gain.setTargetAtTime(monitor ? MONITOR_GAIN : 0, gain.context.currentTime, 0.02);
  }, [monitor, phase]);

  /** Pick the transport: the shared LiveKit room when configured, else legacy. */
  async function start(): Promise<void> {
    setPhase("starting");
    setMessage(null);
    if (livekit?.enabled) {
      await startLiveKit();
    } else {
      await startLegacy();
    }
  }

  /**
   * WebRTC publish into the shared studio room.
   *
   * The same room an OBS DJ joins over WHIP: one SFU in front of the station,
   * one feed coming back through the egress worker, and no transport-specific
   * handling on the station side. The station goes on air a couple of seconds
   * later, once the egress worker has attached its RTMP push.
   */
  async function startLiveKit(): Promise<void> {
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let room: Room | null = null;
    try {
      const cfg = livekit;
      if (!cfg) throw new Error("LiveKit is not configured.");

      // Clean capture: the browser's voice pipeline (echo cancellation, noise
      // suppression, AGC) is tuned for speech and audibly muffs music — levels
      // are the station's loudness rider's job. Headphones are what make
      // skipping echo cancellation safe.
      const audio: MediaTrackConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      };
      if (source === "mic") {
        stream = await navigator.mediaDevices.getUserMedia({ audio });
      } else if (source === "camera") {
        stream = await navigator.mediaDevices.getUserMedia({
          audio,
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        });
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        if (stream.getAudioTracks().length === 0) {
          throw new Error('No audio in the shared source — pick a tab and enable "Share tab audio".');
        }
      }

      ctx = new AudioContext({ sampleRate: 48_000 });
      const input = ctx.createMediaStreamSource(stream);
      // Keep the graph pulled without monitoring: silence to the speakers, so
      // the microphone can never feed back into the room on its own.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      input.connect(sink);
      sink.connect(ctx.destination);
      const monitorGain = ctx.createGain();
      monitorGain.gain.value = monitor ? MONITOR_GAIN : 0;
      input.connect(monitorGain);
      monitorGain.connect(ctx.destination);
      await ctx.resume();

      // Input meter straight off the captured stream — independent of which
      // transport carries it.
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      input.connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      const meter = window.setInterval(() => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        let count = 0;
        for (let i = 0; i < samples.length; i += 64) {
          const value = samples[i] ?? 0;
          sum += value * value;
          count += 1;
        }
        setLevel(Math.min(1, Math.sqrt(sum / Math.max(1, count)) * 5));
      }, 100);

      const { user } = await api.consoleConfig();
      const hostName = user.username?.trim() || "Browser DJ";
      const { token, url } = await api.liveToken(hostName);

      room = new Room({ dynacast: true });
      const joined = room;
      joined.on(RoomEvent.Disconnected, () => {
        // The station (or network) dropped us while on air.
        if (sessionRef.current?.room === joined) stop();
      });
      await joined.connect(url, token);
      for (const track of stream.getTracks()) {
        await joined.localParticipant.publishTrack(track);
      }

      sessionRef.current = { kind: "livekit", room: joined, ctx, stream, monitorGain, meter };
      setPhase("onair");
      setMessage("Published to the studio room — the station takes the feed on air within a few seconds.");
    } catch (error) {
      if (stream) for (const track of stream.getTracks()) track.stop();
      if (ctx) void ctx.close().catch(() => undefined);
      if (room) {
        try {
          void room.disconnect();
        } catch {
          // Already disconnected.
        }
      }
      setPhase("error");
      setMessage(error instanceof Error ? error.message : String(error));
    }
  }

  async function startLegacy(): Promise<void> {
    setPhase("starting");
    setMessage(null);
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    try {
      const { token } = await api.liveInputToken();
      // One id on both sockets so the camera lands on this exact session.
      const sid = crypto.randomUUID();
      // Clean capture: the browser's voice pipeline (echo cancellation,
      // noise suppression, AGC) is tuned for speech and audibly muffs music —
      // it rolls off the highs and pumps the level, which is exactly what a
      // DJ's set must not sound like. Levels are the station's loudness
      // rider's job, and the on-screen guidance already says to use
      // headphones, which is what makes skipping echo cancellation safe.
      const audio: MediaTrackConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      };
      if (source === "mic") {
        stream = await navigator.mediaDevices.getUserMedia({ audio });
      } else if (source === "camera") {
        stream = await navigator.mediaDevices.getUserMedia({
          audio,
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        });
      } else {
        // Tab/app share: the picture goes out alongside the audio now.
        stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        if (stream.getAudioTracks().length === 0) {
          throw new Error('No audio in the shared source — pick a tab and enable "Share tab audio".');
        }
      }

      ctx = new AudioContext({ sampleRate: 48_000 });
      await ctx.audioWorklet.addModule("/pcm-worklet.js");
      const node = new AudioWorkletNode(ctx, "pcm-capture");
      const input = ctx.createMediaStreamSource(stream);
      input.connect(node);
      // Keep the graph pulled without monitoring: silence to the speakers, so
      // the microphone can never feed back into the room on its own.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      node.connect(sink);
      sink.connect(ctx.destination);
      // Local monitor: a second tap of the raw input straight to the DJ's own
      // output. It never reaches the broadcast — that path is the worklet →
      // WebSocket — so monitoring cannot double the audio on air.
      const monitorGain = ctx.createGain();
      monitorGain.gain.value = monitor ? MONITOR_GAIN : 0;
      input.connect(monitorGain);
      monitorGain.connect(ctx.destination);
      await ctx.resume();

      const ws = new WebSocket(
        `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}` +
          `/ws/live-input?token=${encodeURIComponent(token)}&sid=${encodeURIComponent(sid)}`,
      );
      ws.binaryType = "arraybuffer";

      let lastMeterAt = 0;
      node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        const pcm = new Int16Array(event.data);
        const now = performance.now();
        if (now - lastMeterAt > 100) {
          lastMeterAt = now;
          let sum = 0;
          let samples = 0;
          for (let i = 0; i < pcm.length; i += 64) {
            const value = (pcm[i] ?? 0) / 32768;
            sum += value * value;
            samples += 1;
          }
          setLevel(Math.min(1, Math.sqrt(sum / Math.max(1, samples)) * 5));
        }
        if (ws.readyState === WebSocket.OPEN) ws.send(event.data);
      };

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = () => reject(new Error("Could not reach the station's live input."));
        ws.onclose = (event) => {
          if (event.code !== 1000) {
            reject(new Error(`Live input refused (code ${event.code}${event.reason ? `: ${event.reason}` : ""})`));
          }
        };
      });
      ws.onclose = () => {
        // The station (or network) dropped us while on air.
        if (sessionRef.current?.ws === ws) stop();
      };

      // ---- camera ---------------------------------------------------------
      let videoWs: WebSocket | null = null;
      let videoEl: HTMLVideoElement | null = null;
      let frameTimer: number | null = null;
      if (stream.getVideoTracks().length > 0) {
        try {
          const socket = new WebSocket(
            `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}` +
              `/ws/live-video?token=${encodeURIComponent(token)}&sid=${encodeURIComponent(sid)}`,
          );
          socket.binaryType = "arraybuffer";
          await new Promise<void>((resolve, reject) => {
            socket.onopen = () => resolve();
            socket.onerror = () => reject(new Error("Could not reach the station's video input."));
            socket.onclose = (event) => {
              if (event.code !== 1000) {
                reject(new Error(`Video input refused (code ${event.code}${event.reason ? `: ${event.reason}` : ""})`));
              }
            };
          });
          videoWs = socket;

          const element = document.createElement("video");
          element.srcObject = new MediaStream([stream.getVideoTracks()[0] as MediaStreamTrack]);
          element.muted = true;
          element.playsInline = true;
          element.autoplay = true;
          await element.play();
          videoEl = element;
          frameTimer = startFramePump(element, socket);
        } catch (error) {
          // Losing the camera must not lose the set: carry on audio-only.
          setMessage(
            `Camera unavailable (${error instanceof Error ? error.message : "unknown error"}) — broadcasting audio only.`,
          );
          if (videoWs) {
            try {
              videoWs.close();
            } catch {
              // Already closed.
            }
            videoWs = null;
          }
          for (const track of stream.getVideoTracks()) track.stop();
        }
      }

      sessionRef.current = { kind: "legacy", ws, ctx, stream, videoWs, videoEl, frameTimer, monitorGain };
      setPhase("onair");
    } catch (error) {
      if (stream) for (const track of stream.getTracks()) track.stop();
      if (ctx) void ctx.close().catch(() => undefined);
      setPhase("error");
      setMessage(error instanceof Error ? error.message : String(error));
    }
  }

  const onAirHere = phase === "onair";
  const busy = phase === "starting" || onAirHere;

  return (
    <div className="card__body form">
      <div className="field">
        <span className="field__label">Station status</span>
        <span>
          {live ? (
            <strong>
              ON AIR — {describeLive(live)}
              {live.hasVideo ? " · video" : ""}
            </strong>
          ) : (
            "Automation is on air"
          )}
        </span>
        {onAirHere ? (
          <span className="field__hint">You are broadcasting: speak and watch the meter.</span>
        ) : null}
      </div>

      <div className="field">
        <span className="field__label">Source</span>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: "0.4rem", alignItems: "center", cursor: "pointer" }}>
            <input
              type="radio"
              name="live-source"
              checked={source === "mic"}
              disabled={busy}
              onChange={() => setSource("mic")}
            />
            Microphone
          </label>
          <label style={{ display: "flex", gap: "0.4rem", alignItems: "center", cursor: "pointer" }}>
            <input
              type="radio"
              name="live-source"
              checked={source === "camera"}
              disabled={busy}
              onChange={() => setSource("camera")}
            />
            Camera + mic
          </label>
          <label style={{ display: "flex", gap: "0.4rem", alignItems: "center", cursor: "pointer" }}>
            <input
              type="radio"
              name="live-source"
              checked={source === "tab"}
              disabled={busy}
              onChange={() => setSource("tab")}
            />
            Tab / screen
          </label>
        </div>
        <span className="field__hint">
          Camera and tab sources broadcast their picture as well as their sound.
          Tab audio needs the browser's share dialog with "Share tab audio" ticked.
        </span>
      </div>

      <div className="field">
        <span className="field__label">Level</span>
        <div
          style={{ height: "0.55rem", background: "rgba(255,255,255,0.08)", borderRadius: "0.3rem", overflow: "hidden" }}
          role="meter"
          aria-label="Input level"
          aria-valuenow={Math.round(level * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.round(level * 100)}%`,
              background: "#4dd8cc",
              transition: "width 80ms linear",
            }}
          />
        </div>
      </div>

      <div className="field">
        <span className="field__label">Monitor</span>
        <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={monitor}
            onChange={(event) => setMonitor(event.target.checked)}
          />
          <span>Hear myself while broadcasting</span>
        </label>
        <span className="field__hint">
          Plays your input back through this computer only — it never goes out
          on air twice. Use headphones while it is on: speakers plus an open
          mic is a feedback loop.
        </span>
      </div>

      {livekit?.enabled ? (
        <div className="field">
          <span className="field__label">OBS (WHIP)</span>
          {whip ? (
            <>
              <input type="text" readOnly value={whip.url} aria-label="WHIP server URL" />
              <input type="text" readOnly value={whip.streamKey} aria-label="WHIP stream key" />
              <span className="field__hint">
                OBS → Settings → Stream → Service <strong>Custom</strong> (WHIP): paste the URL
                as the server and the key as the bearer/stream key. OBS and this tab share the
                same studio room — only one of you should be on air at a time.
              </span>
            </>
          ) : (
            <>
              <button
                className="button"
                disabled={whipBusy}
                onClick={() => {
                  setWhipBusy(true);
                  setWhipError(null);
                  api
                    .liveWhip()
                    .then(setWhip)
                    .catch((error: unknown) =>
                      setWhipError(error instanceof Error ? error.message : String(error)),
                    )
                    .finally(() => setWhipBusy(false));
                }}
              >
                {whipBusy ? "Preparing…" : "Get OBS stream settings"}
              </button>
              {whipError ? (
                <span className="field__hint">{whipError}</span>
              ) : (
                <span className="field__hint">
                  Mints the station's stable WHIP endpoint for OBS — the WebRTC path,
                  same room as this console.
                </span>
              )}
            </>
          )}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
        {onAirHere ? (
          <button className="button" onClick={stop}>
            Stop broadcasting
          </button>
        ) : (
          <button
            className="button button--primary"
            disabled={phase === "starting"}
            onClick={() => void start()}
          >
            {phase === "starting" ? "Connecting…" : "Start broadcasting"}
          </button>
        )}
        {message ? <span className="field__hint">{message}</span> : null}
        {phase === "error" && !message ? <span className="field__hint">Could not start.</span> : null}
      </div>

      <span className="field__hint">
        {livekit?.enabled
          ? "Publishing puts the station live within a few seconds; stopping hands it back to automation."
          : "The first packet puts the station live; stopping — or three seconds of silence — hands it back to automation."}
      </span>
    </div>
  );
}

/**
 * Sign-in for a password-protected console.
 *
 * The server guards /api with Basic auth and a fetch() never raises the
 * browser's native login prompt, so without this form a fresh browser would
 * see an empty console and every action would 401 (including the live-input
 * token). Credentials are stored by ../auth.js and attached to every request
 * in api.ts.
 */
function SignInForm({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    if (busy || username.trim().length === 0 || password.length === 0) return;
    setBusy(true);
    setError(null);
    setCredentials(username.trim(), password);
    try {
      // Round-trips against a guarded route: proves the credentials before
      // unlocking the console, so a typo says so immediately.
      await api.consoleConfig();
      onSignedIn();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "";
      const rejected = message.startsWith("Sign in");
      if (rejected) clearCredentials();
      setError(
        rejected
          ? "Wrong username or password."
          : message || "Could not sign in — is the station reachable?",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card__body form">
      <div className="field">
        <span className="field__label">Sign in</span>
        <span className="field__hint">
          This console is password-protected. Use the station admin account, or any
          account an admin added for you.
        </span>
      </div>
      <div className="field">
        <span className="field__label">Username</span>
        <input
          type="text"
          value={username}
          autoComplete="username"
          onChange={(event) => setUsername(event.target.value)}
        />
      </div>
      <div className="field">
        <span className="field__label">Password</span>
        <input
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void submit();
          }}
        />
      </div>
      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
        <button
          className="button button--primary"
          onClick={() => void submit()}
          disabled={busy || username.trim().length === 0 || password.length === 0}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </div>
      {error ? (
        <div className="banner banner--error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export function Console(): JSX.Element {
  const [tab, setTab] = useState<Tab>("library");
  /** checking → probe result: ready (auth off or accepted) / signin (401). */
  const [access, setAccess] = useState<"checking" | "ready" | "signin">("checking");
  const [problem, setProblem] = useState<string | null>(null);
  const [probe, setProbe] = useState(0);
  /** Stored sign-in: only then is there something to sign out of. */
  const hasCredentials = authorizationValue() !== null;

  function signOut(): void {
    clearCredentials();
    setAccess("signin");
  }

  useEffect(() => {
    let cancelled = false;
    setProblem(null);
    api
      .consoleConfig()
      .then(() => {
        if (!cancelled) setAccess("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        const message = cause instanceof Error ? cause.message : String(cause);
        if (message.startsWith("Sign in")) {
          setAccess("signin");
          return;
        }
        // Not an auth problem — the station itself was unreachable. Keep the
        // last access state and offer a retry instead of a login form.
        setProblem(message || "Could not reach the station.");
      });
    return () => {
      cancelled = true;
    };
  }, [probe]);

  let body: JSX.Element;
  if (access === "ready") {
    body = (
      <>
        {tab === "library" ? <LibraryTab /> : null}
        {tab === "schedule" ? <ScheduleTab /> : null}
        {tab === "go live" ? <GoLiveTab /> : null}
        {tab === "settings" ? <SettingsTab /> : null}
      </>
    );
  } else if (access === "signin") {
    body = <SignInForm onSignedIn={() => setAccess("ready")} />;
  } else {
    body = (
      <div className="card__body form">
        <div className="field">
          <span className="field__label">Station console</span>
          <span className="field__hint">{problem ?? "Checking station access…"}</span>
        </div>
        <button className="button button--primary" onClick={() => setProbe((n) => n + 1)}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <section className="card" aria-label="Station console">
      <div className="card__head">
        <h2 className="card__title">Console</h2>
        {access === "ready" ? (
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
            <div className="tabs" role="tablist">
              {(["library", "schedule", "go live", "settings"] as const).map((name) => (
                <button
                  key={name}
                  role="tab"
                  className="tab"
                  aria-selected={tab === name}
                  onClick={() => setTab(name)}
                >
                  {name}
                </button>
              ))}
            </div>
            {hasCredentials ? (
              <button className="tab" onClick={signOut}>
                sign out
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {body}
    </section>
  );
}